/**
 * UIController.ts - Shadow DOM-based UI for Standalone IIFE
 * Implements floating button and configuration panel
 * All styles injected inline for single-file portability
 * 
 * Two-Step Interaction Model:
 * - First click on FAB: Initialize engine (call onActivate)
 * - Subsequent clicks: Toggle configuration panel
 */

import type { AtmosphereConfig } from '@core/StateManager';
import { SNOW_COLORS } from '@core/Constants';
import type { SnowColor } from '@core/Constants';

export type MuteDuration = 'day' | '3days' | 'forever';

// Re-export for consumers
export { SNOW_COLORS };
export type { SnowColor };

export interface UIControllerOptions {
  benchmarkCode?: string;         // Performance benchmark code: 'H', 'M', or 'L'
  onActivate?: () => void;           // Called on first click to initialize engine
  onIntensityChange?: (intensity: number) => void;
  onWindChange?: (strength: number) => void;    // -5.0 to 5.0 directional wind
  onColorChange?: (color: SnowColor) => void;   // Color selection callback
  onAccumulationToggle?: (enabled: boolean) => void;  // Toggle snow ground accumulation
  onMute?: (duration: MuteDuration) => void;
  onToggle?: (enabled: boolean) => void;
}

// Max-safe z-index for UI overlay
const MAX_Z_INDEX = 2147483647;

// Inline CSS styles for Shadow DOM encapsulation
const UI_STYLES = `
  :host {
    all: initial;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
    position: fixed !important;
    z-index: ${MAX_Z_INDEX} !important;
  }

  * {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  /* Falling FAB Container - positioned for animation */
  .atmosphere-fab-wrapper {
    position: fixed;
    right: 20px;
    z-index: ${MAX_Z_INDEX} !important;
    pointer-events: auto;
  }

  /* Falling animation keyframes */
  @keyframes snowflakeFall {
    0% {
      top: -70px;
      opacity: 0;
    }
    5% {
      opacity: 1;
    }
    95% {
      opacity: 1;
    }
    100% {
      top: calc(100vh + 20px);
      opacity: 0;
    }
  }

  /* Horizontal sway animation */
  @keyframes snowflakeSway {
    0%, 100% {
      transform: translateX(0);
    }
    25% {
      transform: translateX(-15px);
    }
    75% {
      transform: translateX(15px);
    }
  }

  .atmosphere-fab-wrapper.falling {
    animation: 
      snowflakeFall 18s linear infinite,
      snowflakeSway 4s ease-in-out infinite;
  }

  .atmosphere-fab-wrapper.falling:hover {
    animation-play-state: paused;
  }

  .atmosphere-ui-container {
    position: fixed;
    bottom: 20px;
    right: 20px;
    z-index: ${MAX_Z_INDEX} !important;
    font-size: 14px;
    line-height: 1.4;
  }

  /* Floating Button */
  .atmosphere-fab {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.25) 0%, rgba(255, 255, 255, 0.1) 100%);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    border: 1px solid rgba(255, 255, 255, 0.3);
    box-shadow: 
      0 8px 32px rgba(0, 0, 0, 0.1),
      0 4px 16px rgba(0, 0, 0, 0.05),
      inset 0 1px 0 rgba(255, 255, 255, 0.4);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    outline: none;
    pointer-events: auto;
  }

  .atmosphere-fab:hover {
    transform: scale(1.1);
    box-shadow: 
      0 12px 40px rgba(0, 0, 0, 0.15),
      0 6px 20px rgba(0, 0, 0, 0.1),
      inset 0 1px 0 rgba(255, 255, 255, 0.5);
  }

  .atmosphere-fab:active {
    transform: scale(0.95);
  }

  .atmosphere-fab.disabled {
    opacity: 0.5;
    filter: grayscale(0.5);
  }

  /* Active state - engine is running */
  .atmosphere-fab.active {
    background: linear-gradient(135deg, rgba(99, 102, 241, 0.4) 0%, rgba(79, 70, 229, 0.3) 100%);
    border-color: rgba(99, 102, 241, 0.5);
    box-shadow: 
      0 8px 32px rgba(99, 102, 241, 0.2),
      0 4px 16px rgba(99, 102, 241, 0.1),
      inset 0 1px 0 rgba(255, 255, 255, 0.4);
  }

  .atmosphere-fab.active:hover {
    box-shadow: 
      0 12px 40px rgba(99, 102, 241, 0.25),
      0 6px 20px rgba(99, 102, 241, 0.15),
      inset 0 1px 0 rgba(255, 255, 255, 0.5);
  }

  /* Pulsing animation for inactive FAB (hint to click) */
  .atmosphere-fab:not(.active) {
    animation: pulse 2s ease-in-out infinite;
  }

  @keyframes pulse {
    0%, 100% {
      box-shadow: 
        0 8px 32px rgba(0, 0, 0, 0.1),
        0 4px 16px rgba(0, 0, 0, 0.05),
        inset 0 1px 0 rgba(255, 255, 255, 0.4);
    }
    50% {
      box-shadow: 
        0 8px 32px rgba(99, 102, 241, 0.2),
        0 4px 16px rgba(99, 102, 241, 0.1),
        inset 0 1px 0 rgba(255, 255, 255, 0.4),
        0 0 20px rgba(99, 102, 241, 0.3);
    }
  }

  /* Config Panel */
  .atmosphere-panel {
    position: absolute;
    bottom: 70px;
    right: 0;
    width: 280px;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.92) 0%, rgba(248, 250, 252, 0.88) 100%);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.5);
    border-radius: 16px;
    box-shadow: 
      0 16px 64px rgba(0, 0, 0, 0.18),
      0 8px 32px rgba(0, 0, 0, 0.12),
      inset 0 1px 0 rgba(255, 255, 255, 0.6);
    padding: 20px;
    opacity: 0;
    visibility: hidden;
    transform: translateY(10px) scale(0.95);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    color: #1a1a2e;
  }

  .atmosphere-panel.open {
    opacity: 1;
    visibility: visible;
    transform: translateY(0) scale(1);
  }

  .atmosphere-panel-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
    padding-bottom: 12px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  }

  .atmosphere-panel-title {
    font-size: 16px;
    font-weight: 600;
    color: #1a1a2e;
    text-shadow: 0 1px 2px rgba(255, 255, 255, 0.3);
  }

  .atmosphere-close-btn {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.1);
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    color: #1a1a2e;
    transition: all 0.2s ease;
  }

  .atmosphere-close-btn:hover {
    background: rgba(0, 0, 0, 0.2);
  }

  /* Controls */
  .atmosphere-control {
    margin-bottom: 16px;
  }

  .atmosphere-control:last-child {
    margin-bottom: 0;
  }

  .atmosphere-control-label {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
    font-size: 13px;
    font-weight: 500;
    color: #2d2d44;
  }

  .atmosphere-control-value {
    font-size: 12px;
    color: #5a5a7a;
    background: rgba(0, 0, 0, 0.1);
    padding: 2px 8px;
    border-radius: 10px;
  }

  /* Slider */
  .atmosphere-slider {
    width: 100%;
    height: 6px;
    border-radius: 3px;
    background: rgba(0, 0, 0, 0.15);
    outline: none;
    -webkit-appearance: none;
    appearance: none;
    cursor: pointer;
  }

  .atmosphere-slider::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
    cursor: pointer;
    box-shadow: 0 2px 8px rgba(99, 102, 241, 0.4);
    transition: transform 0.2s ease;
  }

  .atmosphere-slider::-webkit-slider-thumb:hover {
    transform: scale(1.15);
  }

  .atmosphere-slider::-moz-range-thumb {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
    cursor: pointer;
    box-shadow: 0 2px 8px rgba(99, 102, 241, 0.4);
    border: none;
  }

  /* Mute Section */
  .atmosphere-mute-section {
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid rgba(255, 255, 255, 0.2);
  }

  .atmosphere-mute-title {
    font-size: 13px;
    font-weight: 500;
    color: #2d2d44;
    margin-bottom: 10px;
  }

  .atmosphere-mute-buttons {
    display: flex;
    gap: 8px;
  }

  .atmosphere-mute-btn {
    flex: 1;
    padding: 8px 4px;
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.3);
    color: #2d2d44;
    font-size: 11px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    text-align: center;
  }

  .atmosphere-mute-btn:hover {
    background: rgba(239, 68, 68, 0.2);
    border-color: rgba(239, 68, 68, 0.3);
    color: #dc2626;
  }

  /* Color Selection */
  .atmosphere-color-section {
    margin-bottom: 16px;
  }

  .atmosphere-color-label {
    font-size: 13px;
    font-weight: 500;
    color: #2d2d44;
    margin-bottom: 10px;
  }

  .atmosphere-color-buttons {
    display: flex;
    gap: 8px;
  }

  .atmosphere-color-btn {
    flex: 1;
    padding: 10px 4px;
    border: 2px solid rgba(0, 0, 0, 0.1);
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.3);
    color: #2d2d44;
    font-size: 10px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    text-align: center;
    position: relative;
    overflow: hidden;
  }

  .atmosphere-color-btn::before {
    content: '';
    position: absolute;
    top: 4px;
    left: 50%;
    transform: translateX(-50%);
    width: 20px;
    height: 20px;
    border-radius: 50%;
    border: 1px solid rgba(0, 0, 0, 0.1);
  }

  .atmosphere-color-btn[data-color="ice-blue"]::before {
    background: rgb(173, 216, 230);
  }

  .atmosphere-color-btn[data-color="white"]::before {
    background: rgb(255, 255, 255);
  }

  .atmosphere-color-btn[data-color="light-grey"]::before {
    background: rgb(211, 211, 211);
  }

  .atmosphere-color-btn span {
    display: block;
    margin-top: 22px;
  }

  .atmosphere-color-btn:hover {
    background: rgba(99, 102, 241, 0.15);
    border-color: rgba(99, 102, 241, 0.3);
  }

  .atmosphere-color-btn.selected {
    background: rgba(99, 102, 241, 0.2);
    border-color: rgba(99, 102, 241, 0.5);
    box-shadow: 0 0 10px rgba(99, 102, 241, 0.2);
  }

  /* Wind Slider (directional) */
  .atmosphere-slider.directional {
    background: linear-gradient(to right, 
      rgba(99, 102, 241, 0.4) 0%, 
      rgba(0, 0, 0, 0.15) 50%, 
      rgba(99, 102, 241, 0.4) 100%
    );
  }

  .atmosphere-wind-indicators {
    display: flex;
    justify-content: space-between;
    font-size: 10px;
    color: #5a5a7a;
    margin-top: 4px;
  }

  /* Toggle Switch */
  .atmosphere-toggle {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 0;
  }

  .atmosphere-toggle-label {
    font-size: 13px;
    font-weight: 500;
    color: #2d2d44;
  }

  .atmosphere-switch {
    position: relative;
    width: 44px;
    height: 24px;
    background: rgba(0, 0, 0, 0.2);
    border-radius: 12px;
    cursor: pointer;
    transition: background 0.3s ease;
  }

  .atmosphere-switch.active {
    background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
  }

  .atmosphere-switch-thumb {
    position: absolute;
    top: 2px;
    left: 2px;
    width: 20px;
    height: 20px;
    background: white;
    border-radius: 50%;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .atmosphere-switch.active .atmosphere-switch-thumb {
    transform: translateX(20px);
  }

  /* Dark mode support */
  @media (prefers-color-scheme: dark) {
    .atmosphere-panel {
      background: linear-gradient(135deg, rgba(24, 24, 38, 0.97) 0%, rgba(18, 18, 32, 0.95) 100%);
      border-color: rgba(255, 255, 255, 0.12);
      color: #f0f0f5;
      box-shadow: 
        0 16px 64px rgba(0, 0, 0, 0.5),
        0 8px 32px rgba(0, 0, 0, 0.4),
        inset 0 1px 0 rgba(255, 255, 255, 0.08);
    }

    .atmosphere-panel-header {
      border-bottom-color: rgba(255, 255, 255, 0.1);
    }

    .atmosphere-panel-title {
      color: #ffffff;
      text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
    }

    .atmosphere-control-label {
      color: #e4e4eb;
    }

    .atmosphere-control-value {
      color: #c4c4d4;
      background: rgba(255, 255, 255, 0.12);
    }

    .atmosphere-close-btn {
      background: rgba(255, 255, 255, 0.1);
      color: #f0f0f5;
    }

    .atmosphere-close-btn:hover {
      background: rgba(255, 255, 255, 0.2);
    }

    .atmosphere-slider {
      background: rgba(255, 255, 255, 0.18);
    }

    .atmosphere-mute-title {
      color: #e4e4eb;
    }

    .atmosphere-mute-btn {
      background: rgba(255, 255, 255, 0.1);
      border-color: rgba(255, 255, 255, 0.15);
      color: #e4e4eb;
    }

    .atmosphere-mute-btn:hover {
      background: rgba(239, 68, 68, 0.35);
      border-color: rgba(239, 68, 68, 0.5);
      color: #fecaca;
    }

    .atmosphere-toggle-label {
      color: #e4e4eb;
    }

    .atmosphere-switch {
      background: rgba(255, 255, 255, 0.18);
    }

    .atmosphere-color-label {
      color: #e4e4eb;
    }

    .atmosphere-color-btn {
      background: rgba(255, 255, 255, 0.1);
      border-color: rgba(255, 255, 255, 0.15);
      color: #e4e4eb;
    }

    .atmosphere-color-btn:hover {
      background: rgba(99, 102, 241, 0.3);
      border-color: rgba(99, 102, 241, 0.5);
    }

    .atmosphere-color-btn.selected {
      background: rgba(99, 102, 241, 0.35);
      border-color: rgba(99, 102, 241, 0.7);
    }

    .atmosphere-wind-indicators {
      color: #b4b4c4;
    }

    .atmosphere-mute-section {
      border-top-color: rgba(255, 255, 255, 0.1);
    }
  }

  /* Animations */
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .atmosphere-ui-container {
    animation: fadeIn 0.4s ease-out;
  }

  /* Panel Footer */
  .atmosphere-panel-footer {
    margin-top: 16px;
    padding-top: 12px;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    text-align: center;
    font-size: 10px;
    color: #888;
  }

  @media (prefers-color-scheme: dark) {
    .atmosphere-panel-footer {
      color: #666;
    }
  }
`;

export class UIController {
  private shadowHost: HTMLElement | null = null;
  private shadowRoot: ShadowRoot | null = null;
  private panelElement: HTMLElement | null = null;
  private fabElement: HTMLElement | null = null;
  private fabWrapper: HTMLElement | null = null;
  private isPanelOpen: boolean = false;
  private isEnabled: boolean = true;
  private isAccumulationEnabled: boolean = true;  // Track accumulation toggle state
  private isEngineActive: boolean = false;  // Track if engine has been activated
  private options: UIControllerOptions;
  private currentConfig: Partial<AtmosphereConfig> = {};
  private selectedColorIndex: number = 0;  // Ice Blue is default
  private fallAnimationId: number | null = null;

  constructor(options: UIControllerOptions = {}) {
    this.options = options;
  }

  /**
   * Inject the UI into the DOM using Shadow DOM
   */
  inject(): void {
    if (this.shadowHost) {
      console.warn('[Atmosphere UI] Already injected');
      return;
    }

    // Create shadow host element
    this.shadowHost = document.createElement('div');
    this.shadowHost.id = 'atmosphere-ui-host';
    this.shadowHost.style.cssText = `
      position: fixed !important;
      z-index: ${MAX_Z_INDEX} !important;
      pointer-events: none;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
    `;

    // Attach shadow DOM
    this.shadowRoot = this.shadowHost.attachShadow({ mode: 'closed' });

    // Inject styles
    const styleElement = document.createElement('style');
    styleElement.textContent = UI_STYLES;
    this.shadowRoot.appendChild(styleElement);

    // Create falling FAB wrapper (for animation)
    this.fabWrapper = this.createFallingFab();
    this.shadowRoot.appendChild(this.fabWrapper);

    // Create UI container (for panel only, positioned at bottom-right)
    const container = this.createUIContainer();
    this.shadowRoot.appendChild(container);

    // Append to body
    document.body.appendChild(this.shadowHost);

    // Setup event listeners
    this.setupEventListeners();

    // Start falling animation
    this.startFallingAnimation();
  }

  /**
   * Create the falling FAB wrapper with snowflake icon
   */
  private createFallingFab(): HTMLElement {
    const wrapper = document.createElement('div');
    wrapper.className = 'atmosphere-fab-wrapper falling';
    wrapper.style.pointerEvents = 'auto';
    
    // Randomize initial horizontal position within right 10% of viewport
    const viewportWidth = window.innerWidth;
    const zoneWidth = viewportWidth * 0.1;  // 10% zone
    const randomX = Math.random() * zoneWidth;
    wrapper.style.right = `${20 + randomX}px`;  // 20px base margin + random offset

    // Stop event propagation
    const stopPropagation = (e: Event) => e.stopPropagation();
    wrapper.addEventListener('click', stopPropagation);
    wrapper.addEventListener('pointerdown', stopPropagation);
    wrapper.addEventListener('mousedown', stopPropagation);
    wrapper.addEventListener('touchstart', stopPropagation);

    // Floating Action Button
    this.fabElement = document.createElement('button');
    this.fabElement.className = 'atmosphere-fab';
    this.fabElement.innerHTML = '❄️';
    this.fabElement.setAttribute('aria-label', 'Atmosphere Snow Controls');
    this.fabElement.setAttribute('title', 'Click to start snow');

    wrapper.appendChild(this.fabElement);

    return wrapper;
  }

  /**
   * Start the falling animation with position resets
   */
  private startFallingAnimation(): void {
    if (!this.fabWrapper) return;

    const resetPosition = () => {
      if (!this.fabWrapper) return;
      
      // Randomize horizontal position within right 10% of viewport
      const viewportWidth = window.innerWidth;
      const zoneWidth = viewportWidth * 0.1;  // 10% zone
      const randomX = Math.random() * zoneWidth;
      this.fabWrapper.style.right = `${20 + randomX}px`;
    };

    // Listen for animation iteration to reset position
    this.fabWrapper.addEventListener('animationiteration', (e) => {
      if ((e as AnimationEvent).animationName === 'snowflakeFall') {
        resetPosition();
      }
    });

    // Handle window resize
    window.addEventListener('resize', () => {
      // Keep within bounds after resize
      if (this.fabWrapper) {
        const viewportWidth = window.innerWidth;
        const zoneWidth = viewportWidth * 0.1;
        const currentRight = parseInt(this.fabWrapper.style.right || '20', 10);
        if (currentRight > 20 + zoneWidth) {
          this.fabWrapper.style.right = `${20 + Math.random() * zoneWidth}px`;
        }
      }
    });
  }

  /**
   * Create the UI container with panel (FAB is now separate)
   */
  private createUIContainer(): HTMLElement {
    const container = document.createElement('div');
    container.className = 'atmosphere-ui-container';
    container.style.pointerEvents = 'auto';
    container.style.zIndex = `${MAX_Z_INDEX}`;

    // Stop event propagation to prevent host page interactions
    // This ensures clicking on our UI doesn't trigger underlying page elements
    const stopPropagation = (e: Event) => e.stopPropagation();
    container.addEventListener('click', stopPropagation);
    container.addEventListener('pointerdown', stopPropagation);
    container.addEventListener('mousedown', stopPropagation);
    container.addEventListener('touchstart', stopPropagation);

    // Config Panel
    this.panelElement = this.createPanel();

    container.appendChild(this.panelElement);

    return container;
  }

  /**
   * Create the configuration panel
   */
  private createPanel(): HTMLElement {
    const panel = document.createElement('div');
    panel.className = 'atmosphere-panel';

    // Additional event propagation stopping for panel interactions
    const stopPropagation = (e: Event) => e.stopPropagation();
    panel.addEventListener('click', stopPropagation);
    panel.addEventListener('pointerdown', stopPropagation);
    panel.addEventListener('mousedown', stopPropagation);
    panel.addEventListener('touchstart', stopPropagation);

    panel.innerHTML = `
      <div class="atmosphere-panel-header">
        <span class="atmosphere-panel-title">❄️ Snow Settings</span>
        <button class="atmosphere-close-btn" aria-label="Close panel">✕</button>
      </div>

      <div class="atmosphere-toggle">
        <span class="atmosphere-toggle-label">Enable Snow</span>
        <div class="atmosphere-switch active" data-control="toggle">
          <div class="atmosphere-switch-thumb"></div>
        </div>
      </div>

      <div class="atmosphere-control">
        <div class="atmosphere-control-label">
          <span>Intensity</span>
          <span class="atmosphere-control-value" data-value="intensity">70%</span>
        </div>
        <input type="range" class="atmosphere-slider" data-control="intensity" 
               min="0" max="100" value="70">
      </div>

      <div class="atmosphere-control">
        <div class="atmosphere-control-label">
          <span>Wind Direction</span>
          <span class="atmosphere-control-value" data-value="wind">None</span>
        </div>
        <input type="range" class="atmosphere-slider directional" data-control="wind" 
               min="-50" max="50" value="0" step="1">
        <div class="atmosphere-wind-indicators">
          <span>← Left</span>
          <span>Right →</span>
        </div>
      </div>

      <div class="atmosphere-color-section">
        <div class="atmosphere-color-label">Color</div>
        <div class="atmosphere-color-buttons">
          <button class="atmosphere-color-btn selected" data-color="ice-blue" data-index="0">
            <span>Ice Blue</span>
          </button>
          <button class="atmosphere-color-btn" data-color="white" data-index="1">
            <span>White</span>
          </button>
          <button class="atmosphere-color-btn" data-color="light-grey" data-index="2">
            <span>Grey</span>
          </button>
        </div>
      </div>

      <div class="atmosphere-toggle">
        <span class="atmosphere-toggle-label">Snow Accumulation</span>
        <div class="atmosphere-switch active" data-control="accumulation">
          <div class="atmosphere-switch-thumb"></div>
        </div>
      </div>

      <div class="atmosphere-mute-section">
        <div class="atmosphere-mute-title">Mute Snow</div>
        <div class="atmosphere-mute-buttons">
          <button class="atmosphere-mute-btn" data-mute="day">1 Day</button>
          <button class="atmosphere-mute-btn" data-mute="3days">3 Days</button>
          <button class="atmosphere-mute-btn" data-mute="forever">Forever</button>
        </div>
      </div>

      <div class="atmosphere-panel-footer">
        ${__ATMOSPHERE_BUILD_TIME__}${this.options.benchmarkCode || ''}
      </div>
    `;

    return panel;
  }

  /**
   * Setup event listeners for UI controls
   */
  private setupEventListeners(): void {
    if (!this.shadowRoot) return;

    // FAB click - two-step interaction
    // First click: activate engine
    // Subsequent clicks: toggle panel
    this.fabElement?.addEventListener('click', (e) => {
      e.stopPropagation();
      
      if (!this.isEngineActive) {
        // First click - activate the engine
        this.options.onActivate?.();
        // Note: setEngineActive(true) will be called by the Atmosphere class after engine is ready
      } else {
        // Subsequent clicks - toggle panel
        this.togglePanel();
      }
    });

    // Also stop propagation on pointerdown for FAB
    this.fabElement?.addEventListener('pointerdown', (e) => e.stopPropagation());
    this.fabElement?.addEventListener('mousedown', (e) => e.stopPropagation());

    // Close button
    const closeBtn = this.shadowRoot.querySelector('.atmosphere-close-btn');
    closeBtn?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.closePanel();
    });
    closeBtn?.addEventListener('pointerdown', (e) => e.stopPropagation());

    // Toggle switch
    const toggleSwitch = this.shadowRoot.querySelector('[data-control="toggle"]');
    toggleSwitch?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.isEnabled = !this.isEnabled;
      toggleSwitch.classList.toggle('active', this.isEnabled);
      this.fabElement?.classList.toggle('disabled', !this.isEnabled);
      this.options.onToggle?.(this.isEnabled);
    });
    toggleSwitch?.addEventListener('pointerdown', (e) => e.stopPropagation());

    // Accumulation toggle switch
    const accumulationSwitch = this.shadowRoot.querySelector('[data-control="accumulation"]');
    accumulationSwitch?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.isAccumulationEnabled = !this.isAccumulationEnabled;
      accumulationSwitch.classList.toggle('active', this.isAccumulationEnabled);
      this.options.onAccumulationToggle?.(this.isAccumulationEnabled);
    });
    accumulationSwitch?.addEventListener('pointerdown', (e) => e.stopPropagation());

    // Intensity slider
    const intensitySlider = this.shadowRoot.querySelector('[data-control="intensity"]') as HTMLInputElement;
    const intensityValue = this.shadowRoot.querySelector('[data-value="intensity"]');
    intensitySlider?.addEventListener('input', (e) => {
      e.stopPropagation();
      const value = parseInt(intensitySlider.value, 10);
      if (intensityValue) {
        intensityValue.textContent = `${value}%`;
      }
      this.options.onIntensityChange?.(value / 100);
    });
    intensitySlider?.addEventListener('pointerdown', (e) => e.stopPropagation());
    intensitySlider?.addEventListener('click', (e) => e.stopPropagation());

    // Wind slider (directional: -5.0 to 5.0)
    const windSlider = this.shadowRoot.querySelector('[data-control="wind"]') as HTMLInputElement;
    const windValue = this.shadowRoot.querySelector('[data-value="wind"]');
    windSlider?.addEventListener('input', (e) => {
      e.stopPropagation();
      const sliderValue = parseInt(windSlider.value, 10);
      // Convert slider value (-50 to 50) to wind strength (-5.0 to 5.0)
      const windStrength = sliderValue / 10;
      
      if (windValue) {
        if (sliderValue === 0) {
          windValue.textContent = 'None';
        } else if (sliderValue < 0) {
          windValue.textContent = `← ${Math.abs(windStrength).toFixed(1)}`;
        } else {
          windValue.textContent = `${windStrength.toFixed(1)} →`;
        }
      }
      this.options.onWindChange?.(windStrength);
    });
    windSlider?.addEventListener('pointerdown', (e) => e.stopPropagation());
    windSlider?.addEventListener('click', (e) => e.stopPropagation());

    // Color buttons
    const colorButtons = this.shadowRoot.querySelectorAll('[data-color]');
    colorButtons.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const index = parseInt(btn.getAttribute('data-index') || '0', 10);
        this.selectedColorIndex = index;
        
        // Update selected state
        colorButtons.forEach((b) => b.classList.remove('selected'));
        btn.classList.add('selected');
        
        // Notify callback
        this.options.onColorChange?.(SNOW_COLORS[index]);
      });
      btn.addEventListener('pointerdown', (e) => e.stopPropagation());
    });

    // Mute buttons
    const muteButtons = this.shadowRoot.querySelectorAll('[data-mute]');
    muteButtons.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const duration = btn.getAttribute('data-mute') as MuteDuration;
        this.options.onMute?.(duration);
      });
      btn.addEventListener('pointerdown', (e) => e.stopPropagation());
    });

    // Close panel when clicking outside
    document.addEventListener('click', (e) => {
      if (this.isPanelOpen && !this.shadowHost?.contains(e.target as Node)) {
        this.closePanel();
      }
    });

    // Keyboard support
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isPanelOpen) {
        this.closePanel();
      }
    });
  }

  /**
   * Set engine active state (called after engine is initialized)
   * Updates button appearance and enables panel toggling
   */
  setEngineActive(active: boolean): void {
    this.isEngineActive = active;
    
    if (this.fabElement) {
      this.fabElement.classList.toggle('active', active);
      this.fabElement.setAttribute('title', active ? 'Click to open settings' : 'Click to start snow');
    }
  }

  /**
   * Toggle the panel open/closed
   */
  private togglePanel(): void {
    if (this.isPanelOpen) {
      this.closePanel();
    } else {
      this.openPanel();
    }
  }

  /**
   * Open the panel
   */
  private openPanel(): void {
    if (!this.panelElement) return;
    this.panelElement.classList.add('open');
    this.isPanelOpen = true;
    
    // Hide the falling FAB when panel is open
    if (this.fabWrapper) {
      this.fabWrapper.style.display = 'none';
    }
  }

  /**
   * Close the panel
   */
  private closePanel(): void {
    if (!this.panelElement) return;
    this.panelElement.classList.remove('open');
    this.isPanelOpen = false;
    
    // Show the falling FAB when panel is closed
    if (this.fabWrapper) {
      this.fabWrapper.style.display = 'block';
    }
  }

  /**
   * Update UI to reflect current config
   */
  updateFromConfig(config: Partial<AtmosphereConfig>): void {
    this.currentConfig = config;
    if (!this.shadowRoot) return;

    // Update toggle
    const toggleSwitch = this.shadowRoot.querySelector('[data-control="toggle"]');
    if (toggleSwitch && config.enabled !== undefined) {
      this.isEnabled = config.enabled;
      toggleSwitch.classList.toggle('active', this.isEnabled);
      this.fabElement?.classList.toggle('disabled', !this.isEnabled);
    }

    // Update accumulation toggle
    const accumulationSwitch = this.shadowRoot.querySelector('[data-control="accumulation"]');
    if (accumulationSwitch && config.snow?.accumulation?.enabled !== undefined) {
      this.isAccumulationEnabled = config.snow.accumulation.enabled;
      accumulationSwitch.classList.toggle('active', this.isAccumulationEnabled);
    }

    // Update intensity slider
    if (config.snow?.intensity !== undefined) {
      const slider = this.shadowRoot.querySelector('[data-control="intensity"]') as HTMLInputElement;
      const valueDisplay = this.shadowRoot.querySelector('[data-value="intensity"]');
      if (slider) {
        const value = Math.round(config.snow.intensity * 100);
        slider.value = String(value);
        if (valueDisplay) {
          valueDisplay.textContent = `${value}%`;
        }
      }
    }

    // Update wind slider (directional: -5.0 to 5.0)
    if (config.snow?.wind?.strength !== undefined) {
      const slider = this.shadowRoot.querySelector('[data-control="wind"]') as HTMLInputElement;
      const valueDisplay = this.shadowRoot.querySelector('[data-value="wind"]');
      if (slider) {
        // Convert wind strength (-5.0 to 5.0) to slider value (-50 to 50)
        const sliderValue = Math.round(config.snow.wind.strength * 10);
        slider.value = String(sliderValue);
        if (valueDisplay) {
          if (sliderValue === 0) {
            valueDisplay.textContent = 'None';
          } else if (sliderValue < 0) {
            valueDisplay.textContent = `← ${Math.abs(config.snow.wind.strength).toFixed(1)}`;
          } else {
            valueDisplay.textContent = `${config.snow.wind.strength.toFixed(1)} →`;
          }
        }
      }
    }

    // Update color selection
    if (config.snow?.color) {
      const { r, g, b } = config.snow.color;
      const colorButtons = this.shadowRoot.querySelectorAll('[data-color]');
      
      // Find matching color
      let matchIndex = -1;
      SNOW_COLORS.forEach((color, index) => {
        if (color.r === r && color.g === g && color.b === b) {
          matchIndex = index;
        }
      });
      
      if (matchIndex >= 0) {
        this.selectedColorIndex = matchIndex;
        colorButtons.forEach((btn, idx) => {
          const btnIndex = parseInt(btn.getAttribute('data-index') || '0', 10);
          btn.classList.toggle('selected', btnIndex === matchIndex);
        });
      }
    }
  }

  /**
   * Remove the UI from the DOM
   */
  remove(): void {
    // Cancel any animation frame
    if (this.fallAnimationId !== null) {
      cancelAnimationFrame(this.fallAnimationId);
      this.fallAnimationId = null;
    }
    
    if (this.shadowHost && this.shadowHost.parentNode) {
      this.shadowHost.parentNode.removeChild(this.shadowHost);
    }
    this.shadowHost = null;
    this.shadowRoot = null;
    this.panelElement = null;
    this.fabElement = null;
    this.fabWrapper = null;
    this.isEngineActive = false;
  }

  /**
   * Check if UI is injected
   */
  isInjected(): boolean {
    return this.shadowHost !== null;
  }

  /**
   * Check if engine is active
   */
  getIsEngineActive(): boolean {
    return this.isEngineActive;
  }
}
